import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { CustomerReportCellsService } from '../../../services/customer-report-cells.service';
import { CustomerReportCellsRebuildResult } from '../../../models/api/customerReportCellsRebuildResult';
import { CustomerReportRebuildLog } from '../../../models/api/customerReportRebuildLog';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';

@Component({
  selector: 'app-customer-report-cells',
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, NgxSpinnerModule],
  templateUrl: './customer-report-cells.component.html',
  styleUrl: './customer-report-cells.component.scss'
})
export class CustomerReportCellsComponent implements OnInit {
  private service = inject(CustomerReportCellsService);
  private fb = inject(FormBuilder);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationService = inject(ConfirmationDialogService);

  rebuilding = false;
  result: CustomerReportCellsRebuildResult | null = null;
  error: string | null = null;
  startedAt: Date | null = null;

  history: CustomerReportRebuildLog[] = [];
  get lastRebuild(): CustomerReportRebuildLog | null {
    return this.history.length > 0 ? this.history[0] : null;
  }
  readonly displayedHistoryColumns = ['executedAt', 'trigger', 'triggeredBy', 'rowsInserted', 'elapsedMs'];

  form: FormGroup = this.fb.group({
    fromYear: this.fb.control<number | null>(null, [Validators.min(1900), Validators.max(2100)]),
    toYear: this.fb.control<number | null>(null, [Validators.min(1900), Validators.max(2100)]),
    batchSize: this.fb.control<number>(1000, [Validators.required, Validators.min(1), Validators.max(5000)]),
  });

  readonly displayedTimingColumns = ['category', 'elapsedMs', 'cellsProduced'];

  ngOnInit(): void {
    this.loadHistory();
  }

  private loadHistory(): void {
    this.service.getHistory().subscribe({
      next: (history) => (this.history = history),
      error: () => (this.history = []),
    });
  }

  rebuild(): void {
    if (this.form.invalid || this.rebuilding) {
      return;
    }

    const from = this.form.value.fromYear as number | null;
    const to = this.form.value.toYear as number | null;
    if (from !== null && to !== null && from > to) {
      this.toast.error('From Year must be <= To Year');
      return;
    }

    const yearLabel = (from && to) ? `${from}-${to}` : 'auto-detected range';
    this.confirmationService
      .openConfirmationDialog(
        'Rebuild Customer Reports',
        `This drops and rebuilds the precalculated company-report table over the ` +
        `${yearLabel}. It may take several minutes and will block this browser tab until it completes. ` +
        `The live table is replaced atomically once the rebuild finishes. Continue?`,
        'Rebuild',
        'Cancel',
        'autorenew',
        'clear'
      )
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.runRebuild();
      });
  }

  private runRebuild(): void {
    this.rebuilding = true;
    this.result = null;
    this.error = null;
    this.startedAt = new Date();
    this.spinnerService.show();

    this.service.rebuild({
      fromYear: this.form.value.fromYear ?? undefined,
      toYear: this.form.value.toYear ?? undefined,
      batchSize: this.form.value.batchSize ?? undefined,
    }).subscribe({
      next: (result) => {
        this.result = result;
        this.rebuilding = false;
        this.spinnerService.hide();
        this.toast.success(`Rebuild complete: ${result.rowsInserted.toLocaleString()} rows in ${this.formatDuration(result.totalMs)}`);
        this.loadHistory();
      },
      error: (err) => {
        this.error = err?.error?.error ?? err?.message ?? 'Unknown error';
        this.rebuilding = false;
        this.spinnerService.hide();
        this.toast.error('Rebuild failed');
      }
    });
  }

  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s ${ms % 1000}ms`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  formatNumber(n: number): string {
    return n.toLocaleString();
  }

  triggerLabel(trigger: string): string {
    return trigger === 'Scheduled' ? 'Automatic' : 'Manual';
  }

  triggerDescription(log: CustomerReportRebuildLog): string {
    return log.trigger === 'Scheduled' ? 'Automatic' : `Manual by ${log.triggeredBy}`;
  }
}
